<template>
  <div class="track is-flex is-flex-direction-column is-justify-content-center is-align-items-center">
    <div class="dropdown" ref="dropdown"
      :class="{ 'is-active': isActive, 'is-up': isUp }">
      <div class="dropdown-trigger" @click="toggleIsActive">
        <input v-if="isChangingNumber" type="number"
               v-model="trackNumber" ref="input"
               class="is-size-5 borderless"
               @keydown.enter="isChangingNumber = false" />
        <b v-else class="is-size-4 is-clickable">{{ trackNumber }}</b>
      </div>
      <div class="dropdown-menu track-menu" role="menu" ref="menu">
        <div class="dropdown-content">
          <a class="dropdown-item" @click="isChangingNumber = true">
            <mdicon name="rename-outline" size="18"></mdicon>
            <span>Rename</span>
          </a>
          <a class="dropdown-item" @click="deleteTrack">
            <mdicon name="delete-outline" size="18"></mdicon>
            <span>Delete</span>
          </a>
          <hr class="dropdown-divider">
          <span v-for="key in customSettingKeys" :key="key"
                class="dropdown-item setting">
            <label>
              {{ $filters.toTitle(key) }}
            </label>
            <template v-if="hasCustomSetting(key)">
              <mdicon name="delete-outline" size="18" @click="deleteCustomSetting(key)"></mdicon>
              <input type="number" class="input is-small" step="0.1"
                     :value="customFireDurations[key][trackNumber] / 1000"
                     @change="customFireDurations[key][trackNumber] = $event.target.value * 1000" />
            </template>
            <button v-else class="button is-small is-warning" @click="addCustomSetting(key)">Add Custom</button>
          </span>
          <hr class="dropdown-divider">
          <a class="dropdown-item" @click="addTrack">
            <mdicon name="plus" size="18"></mdicon>
            <span>Insert below</span>
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'TrackMenu',
  props: {
    trackIndex: Number
  },
  data() {
    return {
      isActive: false,
      isChangingNumber: false,
      isUp: false
    }
  },
  computed: {
    ...mapState(['cannonNumbers', 'customFireDurations']),
    trackNumber: {
      get() {
        return this.cannonNumbers[this.trackIndex]
      },
      set(number) {
        const payload = { index: this.trackIndex, number }
        this.$store.dispatch('renameTrack', payload)
      }
    },
    customSettingKeys() {
      return Object.keys(this.customFireDurations)
    }
  },
  watch: {
    isChangingNumber(v) {
      if (v) {
        this.isActive = false
        this.$nextTick(() => {
          this.$refs.input.focus()
        })
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick)
    document.addEventListener('keydown', this.handleKeydown)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick)
    document.removeEventListener('keydown', this.handleKeydown)
  },
  methods: {
    handleOutsideClick(event) {
      // Use composedPath rather than contains(target): a re-render can detach
      // the clicked element (e.g. the "Add Custom" button) before this fires.
      if (this.isActive && !event.composedPath().includes(this.$refs.dropdown)) {
        this.isActive = false
      }
    },
    handleKeydown(event) {
      if (event.key === 'Escape' && this.isActive) {
        this.isActive = false
      }
    },
    toggleIsActive() {
      if (!this.isChangingNumber) {
        this.isActive = !this.isActive
        if (this.isActive) {
          this.flipIfOffscreen()
        }
      }
    },
    flipIfOffscreen() {
      // Reset so we measure the natural (downward) position first
      this.isUp = false
      this.$nextTick(() => {
        const menu = this.$refs.menu
        if (!menu) return
        const rect = menu.getBoundingClientRect()
        this.isUp = rect.bottom > window.innerHeight
      })
    },
    deleteTrack() {
      this.$store.dispatch('deleteTrack', this.trackIndex)
      this.$emit('redraw')
      this.isActive = false
    },
    addTrack() {
      this.$store.dispatch('addTrack', this.trackIndex)
      this.$emit('redraw')
      this.isActive = false
    },
    addCustomSetting(key) {
      const { trackNumber } = this
      this.$store.commit('addCustomSetting', { key, trackNumber })
    },
    hasCustomSetting(key) {
      return this.trackNumber in this.customFireDurations[key]
    },
    deleteCustomSetting(key) {
      const { trackNumber } = this
      this.$store.commit('deleteCustomSetting', { key, trackNumber })
    }
  }
}
</script>

<style lang="scss" scoped>
input.borderless {
  width: 50px;
  border: none !important;

  &:focus-visible {
    border: none;
    outline: none;
  }
}

span.setting {
  display: flex;

  label {
    flex: 1;
    min-width: 55px;
  }

  input {
    margin: 0 0.5rem;
  }

  .mdi {
    &:hover {
      cursor: pointer;
    }
  }
}
</style>

<style lang="scss">
div.track-menu {
  min-width: unset;
  width: 200px;

  .dropdown-item {
    padding: 0.25rem 0.75rem;
    fill: red;
    span.mdi {
      margin-right: 0.5rem;
      svg {
        fill: #666;
      }
    }
    span:last-child {
      padding-top: 3px;
    }
    display: flex;
    align-items: center;
  }
}
</style>
